import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/messages/{parentMessageId}/replies';

type MessageRepliesApi = ApiDefinition[typeof PATH_TEMPLATE]['GET'];

/** SWR hook for fetching message replies with cursor-based pagination. */
export const useMessageReplies = (
  params: MessageRepliesApi['params'] | null,
  query?: Partial<MessageRepliesApi['query']>,
  config?: UseNoodleSwrConfig<MessageRepliesApi['response']>,
): SWRResponse<MessageRepliesApi['response'], ApiError> => {
  const path = params ? buildPath<MessageRepliesApi>(PATH_TEMPLATE, params) : null;
  const filteredQuery = Object.fromEntries(Object.entries(query ?? {}).filter(([, value]) => value !== undefined));
  const hasQuery = Object.keys(filteredQuery).length > 0;
  const key: SwrKey = path ? (hasQuery ? [path, { params: filteredQuery }] : path) : null;
  return useNoodleSwr(key, {
    revalidateOnFocus: false,
    ...config,
  });
};

/** Fetch message replies with cursor-based pagination. */
const getMessageReplies = async ({
  parentMessageId,
  ...query
}: MessageRepliesApi['params'] & MessageRepliesApi['query']): Promise<MessageRepliesApi['response']> => {
  const path = buildPath<MessageRepliesApi>(PATH_TEMPLATE, { parentMessageId });
  const response = await noodleApiClient.get(path, { params: query });
  return response.data;
};

export default getMessageReplies;
